import React, { useState } from 'react';
import Swal from 'sweetalert2';
import api from '../services/api';
import { getSwalTheme } from '../utils/swalTheme';
import { LISTING_TYPE_LABELS } from '../utils/formatters';
import useModalAccessibility from '../hooks/useModalAccessibility';
import './EditShiftModal.css';

const EditMedallionPostModal = ({ post, onClose, onUpdate, isAdmin = false }) => {
  const modalRef = useModalAccessibility(onClose);
  const apiPath = isAdmin ? '/admin/medallion-posts' : '/medallion-posts';

  const [formData, setFormData] = useState({
    title: post.title,
    description: post.description || '',
    price: post.price || '',
    quantity: post.quantity || 1,
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const submitData = {
        title: formData.title,
        description: formData.description,
        price: formData.price !== '' ? parseFloat(formData.price) : null,
        quantity: formData.quantity ? parseInt(formData.quantity) : 1,
      };

      await api.put(`${apiPath}/${post.id}`, submitData);
      onUpdate();
      onClose();
      Swal.fire({
        ...getSwalTheme(),
        icon: 'success',
        title: 'Medallion Listing Updated!',
        text: 'Your changes have been saved.'
      });
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to update medallion listing');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose} role="presentation">
      <div
        ref={modalRef}
        className="edit-modal-content"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="edit-medallion-title"
      >
        <button
          type="button"
          className="modal-close"
          onClick={onClose}
          aria-label="Close dialog"
        >
          &times;
        </button>

        <div className="modal-header">
          <h2 id="edit-medallion-title">Edit Medallion Listing</h2>
        </div>

        <form onSubmit={handleSubmit} className="edit-shift-form">
          {error && <div className="form-error">{error}</div>}

          <div className="form-group">
            <label>Listing Type</label>
            <div className="pay-type-display">
              {LISTING_TYPE_LABELS[post.listing_type] || post.listing_type}
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="title">Title *</label>
            <input
              type="text"
              id="title"
              name="title"
              value={formData.title}
              onChange={handleChange}
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="description">Description</label>
            <textarea
              id="description"
              name="description"
              value={formData.description}
              onChange={handleChange}
              rows={3}
            />
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="price">Price ($)</label>
              <input
                type="number"
                id="price"
                name="price"
                value={formData.price}
                onChange={handleChange}
                placeholder="Optional"
                min="0"
                step="0.01"
              />
            </div>

            <div className="form-group">
              <label htmlFor="quantity">Quantity</label>
              <input
                type="number"
                id="quantity"
                name="quantity"
                value={formData.quantity}
                onChange={handleChange}
                min="1"
                max="999"
              />
            </div>
          </div>

          <div className="modal-actions">
            <button type="button" className="btn-cancel" onClick={onClose}>
              Cancel
            </button>
            <button type="submit" className="btn-save" disabled={loading}>
              {loading ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditMedallionPostModal;
